import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, CreditCard } from 'lucide-react';

const NexiDemoPayment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const orderId = searchParams.get('orderId');
  const amount = searchParams.get('amount');

  const handlePaymentSuccess = () => {
    setProcessing(true);
    // Extract booking ID from order ID
    const bookingId = orderId.split('_')[0];
    
    // Simulate payment processing
    setTimeout(() => {
      navigate(`/booking-success?booking_id=${bookingId}&gateway=nexi`);
    }, 2000);
  };

  const handlePaymentCancel = () => {
    const bookingId = orderId.split('_')[0];
    navigate(`/booking-cancelled?booking_id=${bookingId}&gateway=nexi`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Nexi XPay - Demo Payment</h1>
            <p className="text-sm text-orange-600 font-medium">⚠️ Demo Mode - No real charges</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Order ID:</span>
              <span className="text-sm font-mono font-semibold text-gray-900">{orderId}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Amount:</span>
              <span className="text-2xl font-bold text-blue-600">€{amount}</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              <strong>Demo Mode Active:</strong> This is a simulated payment page. 
              In production, customers would enter their card details here on Nexi's secure hosted page.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handlePaymentSuccess}
              disabled={processing}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 py-6"
            >
              {processing ? (
                <>Processing...</>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Simulate Successful Payment
                </>
              )}
            </Button>

            <Button
              onClick={handlePaymentCancel}
              variant="outline"
              disabled={processing}
              className="w-full py-6 border-2"
            >
              <XCircle className="w-5 h-5 mr-2" />
              Cancel Payment
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              To use real Nexi XPay payments, register at{' '}
              <a 
                href="https://developer.nexigroup.com/xpaygreece/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                developer.nexigroup.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NexiDemoPayment;
